/**
 * Product Options Panel
 * 
 * 显示产品选项、价格和加购功能的面板组件
 * 具有高扩展性，可以轻松添加新产品
 */
class ProductOptionsPanel extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.productId = this.dataset.productId;
    this.recommendedProductsSelector = null;
    
    // 状态管理
    this.state = {
      selectedOptions: {},
      currentVariant: null,
      recommendedProducts: [], // 推荐产品列表
      specialDelivery: null, // 特惠配送信息
      normalDelivery: null, // 普通配送信息
      isPostalCodeValid: false, // 邮箱验证状态
      canAddSpecialDelivery: false, // 是否可以添加特惠配送
      canAddNormalDelivery: false // 是否可以添加普通配送
    };
    
    this.init();
  }
  
  init() {
    this.loadInitialVariant();
    this.attachEventListeners();
  }
  
  /**
   * 加载初始变体数据
   */
  loadInitialVariant() {
    // 如果有预设的变体ID，则加载该变体
    if (this.variantId) {
      this.state.currentVariant = this.getVariantById(this.variantId);
      this.updatePriceDisplay();
      
      // 检查初始变体的特惠配送
      this.checkSpecialDeliveryForVariant(this.variantId);
      
      // 检查初始变体的普通配送
      this.checkNormalDeliveryForVariant(this.variantId);
    }
  }
  
  /**
   * 根据ID获取变体
   */
  getVariantById(variantId) {
    if (!window.productVariants || !window.productVariants[this.productId]) {
      console.warn('Product variants not found in window object');
      return null;
    }
    
    return window.productVariants[this.productId].find(variant => 
      variant.id.toString() === variantId.toString()
    );
  }
  
  /**
   * 绑定事件监听器
   */
  attachEventListeners() {
    // 监听变体选择变化 - 支持多种可能的事件名称
    const variantChangeEvents = ['variant:changed', 'variantChange', 'product:variantChange'];
    
    variantChangeEvents.forEach(eventName => {
      document.addEventListener(eventName, this.handleVariantChange.bind(this));
    });
    
    // 监听推荐产品选择变化
    document.addEventListener('recommendedProductsSelected', this.handleRecommendedProductsChange.bind(this));
    
    /* 已注释特惠配送和普通配送事件监听
    // 监听特惠配送更新
    document.addEventListener('special-delivery:updated', this.handleSpecialDeliveryUpdate.bind(this));
    
    // 监听特惠配送选项面板更新（包含邮箱验证状态）
    document.addEventListener('special-delivery:options-panel-update', this.handleSpecialDeliveryOptionsUpdate.bind(this));
    
    // 监听普通配送更新
    document.addEventListener('normal-delivery:updated', this.handleNormalDeliveryUpdate.bind(this));
    
    // 监听普通配送选项面板更新
    document.addEventListener('normal-delivery:options-panel-update', this.handleNormalDeliveryOptionsUpdate.bind(this));
    */
    
    // 直接监听产品表单变化
    const productForms = document.querySelectorAll(`form[action*="/cart/add"][data-type="add-to-cart-form"]`);
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        // 使用MutationObserver监听变体ID变化
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              const newVariantId = variantInput.value;
              if (newVariantId) {
                this.updateVariant(newVariantId);
              }
            }
          });
        });
        
        observer.observe(variantInput, { attributes: true });
        
        // 初始检查
        if (variantInput.value) {
          this.updateVariant(variantInput.value);
        }
      }
    });
  }
  
  /**
   * 处理变体变化事件
   */
  handleVariantChange(event) {
    // 检查事件是否包含产品ID和变体信息
    if (event.detail) {
      const { productId, variant, variantId } = event.detail;
      
      // 确保是当前产品的变体变化
      if (productId && productId.toString() === this.productId.toString()) {
        if (variant) {
          this.state.currentVariant = variant;
          this.updatePriceDisplay();
        } else if (variantId) {
          this.updateVariant(variantId);
        }
      }
    }
  }
  
  /**
   * 处理推荐产品选择变化
   */
  handleRecommendedProductsChange(event) {
    const recommendedProductsSelector = event.target.closest?.('recommended-products-selector');

    if (
      !recommendedProductsSelector ||
      String(recommendedProductsSelector.dataset.productId) !== String(this.productId)
    ) return;

    this.recommendedProductsSelector = recommendedProductsSelector;
    this.state.recommendedProducts = event.detail?.selectedProducts || [];
    this.updateRecommendedProductsDisplay();
    this.updatePriceDisplay();
  }
  
  /**
   * 处理特惠配送更新 - 已禁用
   */
  handleSpecialDeliveryUpdate(event) {
    /* 已注释特惠配送更新处理
    if (event.detail) {
      // 检查是否是当前变体的特惠配送更新
      if (event.detail.variantId && this.state.currentVariant && 
          event.detail.variantId.toString() === this.state.currentVariant.id.toString()) {
        this.state.specialDelivery = event.detail.specialDelivery;
        this.updateSpecialDeliveryDisplay();
        this.updatePriceDisplay();
      } else if (!event.detail.variantId) {
        // 如果没有变体ID，说明是清理操作
        this.state.specialDelivery = null;
        this.updateSpecialDeliveryDisplay();
        this.updatePriceDisplay();
      }
    }
    */
  }
  
  /**
   * 处理特惠配送选项面板更新（包含邮箱验证状态） - 已禁用
   */
  handleSpecialDeliveryOptionsUpdate(event) {
    /* 已注释特惠配送选项面板更新处理
    if (event.detail) {
      // 更新邮箱验证状态
      this.state.isPostalCodeValid = event.detail.isPostalCodeValid || false;
      this.state.canAddSpecialDelivery = event.detail.canAddSpecialDelivery || false;
      
      // 只有在邮箱验证通过且可以添加特惠配送时才设置特惠配送信息
      if (this.state.canAddSpecialDelivery && event.detail.specialDelivery) {
        // 检查是否是当前变体的特惠配送更新
        if (!event.detail.variantId || !this.state.currentVariant || 
            event.detail.variantId.toString() === this.state.currentVariant.id.toString()) {
          this.state.specialDelivery = event.detail.specialDelivery;
        }
      } else {
        // 如果不能添加特惠配送，清空特惠配送信息
        this.state.specialDelivery = null;
      }
      
      this.updateSpecialDeliveryDisplay();
      this.updatePriceDisplay();
    }
    */
  }
  
  /**
   * 处理普通配送更新 - 已禁用
   */
  handleNormalDeliveryUpdate(event) {
    /* 已注释普通配送更新处理
    if (event.detail) {
      // 检查是否是当前变体的普通配送更新
      if (event.detail.variantId && this.state.currentVariant && 
          event.detail.variantId.toString() === this.state.currentVariant.id.toString()) {
        this.state.normalDelivery = event.detail.normalDelivery;
        this.updateNormalDeliveryDisplay();
        this.updatePriceDisplay();
      } else if (!event.detail.variantId) {
        // 如果没有变体ID，说明是清理操作
        this.state.normalDelivery = null;
        this.updateNormalDeliveryDisplay();
        this.updatePriceDisplay();
      }
    }
    */
  }
  
  /**
   * 处理普通配送选项面板更新 - 已禁用
   */
  handleNormalDeliveryOptionsUpdate(event) {
    /* 已注释普通配送选项面板更新处理
    if (event.detail) {
      // 更新普通配送状态
      this.state.canAddNormalDelivery = event.detail.canAddNormalDelivery || false;
      //debugger
      // 只有在可以添加普通配送时才设置普通配送信息
      if (this.state.canAddNormalDelivery && event.detail.normalDelivery) {
        // 检查是否是当前变体的普通配送更新
        if (!event.detail.variantId || !this.state.currentVariant || 
            event.detail.variantId.toString() === this.state.currentVariant.id.toString()) {
          this.state.normalDelivery = event.detail.normalDelivery;
        }
      } else {
        // 如果不能添加普通配送，清空普通配送信息
        this.state.normalDelivery = null;
      }
      
      this.updateNormalDeliveryDisplay();
      this.updatePriceDisplay();
    }
    */
  }
  
  /**
   * 根据变体ID更新当前变体
   */
  updateVariant(variantId) {
    const newVariant = this.getVariantById(variantId);
    if (newVariant) {
      this.state.currentVariant = newVariant;
      this.updatePriceDisplay();
      this.updateProductImage(newVariant);
      
      // 检查新变体是否有特惠配送
      this.checkSpecialDeliveryForVariant(variantId);
      
      // 检查新变体是否有普通配送
      this.checkNormalDeliveryForVariant(variantId);
    }
  }
  
  /**
   * 检查指定变体的特惠配送 - 已禁用
   */
  checkSpecialDeliveryForVariant(variantId) {
    /* 已注释特惠配送检查
    // 从全局特惠配送处理器获取信息
    if (window.variantMetafieldsHandler) {
      const specialDelivery = window.variantMetafieldsHandler.getCurrentSpecialDelivery();
      // 只有在邮箱验证通过且可以添加特惠配送时才设置特惠配送信息
      if (this.state.canAddSpecialDelivery && specialDelivery) {
        this.state.specialDelivery = specialDelivery;
      } else {
        this.state.specialDelivery = null;
      }
      this.updateSpecialDeliveryDisplay();
    }
    */
    // 确保特惠配送始终为空
    this.state.specialDelivery = null;
    this.updateSpecialDeliveryDisplay();
  }
  
  /**
   * 检查指定变体的普通配送 - 已禁用
   */
  checkNormalDeliveryForVariant(variantId) {
    /* 已注释普通配送检查
    // 从全局普通配送处理器获取信息
    if (window.variantMetafieldsHandler) {
      const normalDelivery = window.variantMetafieldsHandler.getCurrentNormalDelivery();
      // 只有在可以添加普通配送时才设置普通配送信息
      if (this.state.canAddNormalDelivery && normalDelivery) {
        this.state.normalDelivery = normalDelivery;
      } else {
        this.state.normalDelivery = null;
      }
      this.updateNormalDeliveryDisplay();
    }
    */
    // 确保普通配送始终为空
    this.state.normalDelivery = null;
    this.updateNormalDeliveryDisplay();
  }
  
  /**
   * 更新产品图片
   */
  updateProductImage(variant) {
    if (!variant.featured_image) return;
    
    const productImage = this.querySelector('.product-option-image img');
    if (productImage) {
      productImage.src = variant.featured_image.src;
      productImage.srcset = variant.featured_image.srcset || '';
    }
  }
  
  /**
   * 更新推荐产品显示
   */
  updateRecommendedProductsDisplay() {
    const recommendedSection = this.querySelector('.options-panel-recommended-products-section');
    const recommendedList = this.querySelector('.options-panel-recommended-products-list');
    
    if (!recommendedSection || !recommendedList) return;
    
    // 清空现有内容
    recommendedList.innerHTML = '';
    
    if (this.state.recommendedProducts.length > 0) {
      // 显示推荐产品区域
      recommendedSection.style.display = 'block';
      
      // 添加推荐产品项
      this.state.recommendedProducts.forEach(product => {
        const productItem = this.createRecommendedProductItem(product);
        recommendedList.appendChild(productItem);
      });
    } else {
      // 隐藏推荐产品区域
      recommendedSection.style.display = 'none';
    }
  }
  
  /**
   * 创建推荐产品项
   */
  createRecommendedProductItem(product) {
    const item = document.createElement('div');
    item.className = 'options-panel-recommended-product-item';
    item.dataset.variantId = product.variantId;
    
    // 获取产品价格信息
    const priceInfo = this.getProductPriceInfo(product.variantId);
    const price = priceInfo ? priceInfo.price : 0;
    
    // 使用产品详细信息（如果有的话）
    const productName = product.name || this.getProductName(product.variantId);
    const productImage = product.image || this.getProductImage(product.variantId);
    
    item.innerHTML = `
      <div class="options-panel-recommended-product-image">
        <img src="${productImage}" 
             alt="${productName}" 
             width="30" height="30" loading="lazy">
      </div>
      <div class="options-panel-recommended-product-info">
        <span class="options-panel-recommended-product-name">${productName}</span>
        <span class="options-panel-recommended-product-price">${this.formatMoney(price)}</span>
      </div>
      <div class="options-panel-recommended-product-quantity">
        <span class="quantity-label">Quantity:</span>
        <span class="quantity-value">${product.quantity}</span>
      </div>
    `;
    
    return item;
  }
  
  /**
   * 更新特惠配送显示 - 已禁用
   */
  updateSpecialDeliveryDisplay() {
    const specialDeliverySection = this.querySelector('.special-delivery-section');
    
    if (!specialDeliverySection) return;
    
    // 始终隐藏特惠配送区域
    specialDeliverySection.style.display = 'none';
    
    /* 已注释特惠配送显示逻辑
    // 只有在邮箱验证通过且可以添加特惠配送时才显示
    if (this.state.canAddSpecialDelivery && this.state.specialDelivery) {
      // 显示特惠配送区域
      specialDeliverySection.style.display = 'block';
      
      // 更新特惠配送信息
      const nameElement = specialDeliverySection.querySelector('.special-delivery-name');
      const priceElement = specialDeliverySection.querySelector('.special-delivery-price');
      
      if (nameElement) {
        nameElement.textContent = this.state.specialDelivery.name || 'Powerness Special Offer delivery';
      }
      
      if (priceElement) {
        const price = this.state.specialDelivery.price || 0;
        priceElement.textContent = price > 0 ? this.formatMoney(price) : '';
      }
    } else {
      // 隐藏特惠配送区域
      specialDeliverySection.style.display = 'none';
    }
    */
  }
  
  /**
   * 更新普通配送显示 - 已禁用
   */
  updateNormalDeliveryDisplay() {
    const normalDeliverySection = this.querySelector('.normal-delivery-section');
    
    if (!normalDeliverySection) return;
    
    // 始终隐藏普通配送区域
    normalDeliverySection.style.display = 'none';
    
    /* 已注释普通配送显示逻辑
    // 只有在可以添加普通配送时才显示
    if (this.state.canAddNormalDelivery && this.state.normalDelivery) {
      // 显示普通配送区域
      normalDeliverySection.style.display = 'block';
      
      // 更新普通配送信息
      const nameElement = normalDeliverySection.querySelector('.normal-delivery-name');
      const priceElement = normalDeliverySection.querySelector('.normal-delivery-price');
      
      if (nameElement) {
        nameElement.textContent = this.state.normalDelivery.name || 'Standard Delivery';
      }
      
      if (priceElement) {
        const price = this.state.normalDelivery.price || 0;
        priceElement.textContent = price > 0 ? this.formatMoney(price) : '';
      }
    } else {
      // 隐藏普通配送区域
      normalDeliverySection.style.display = 'none';
    }
    */
  }
  
  /**
   * 获取产品价格信息
   */
  getProductPriceInfo(variantId) {
    return this.recommendedProductsSelector?.currentPrices?.get(Number(variantId)) || null;
  }
  
  /**
   * 获取产品图片
   */
  getProductImage(variantId) {
    // 从推荐产品选择器获取图片信息
    const recommendedItems = document.querySelectorAll(`[data-variant-id="${variantId}"]`);
    for (let item of recommendedItems) {
      const img = item.querySelector('img');
      if (img) {
        const imageUrl =
          img.currentSrc ||
          img.getAttribute('src') ||
          img.dataset.srcset ||
          '';

        if (imageUrl) {
          return imageUrl;
        }
      }
    }
    
    // 尝试从推荐产品列表项获取图片
    const recommendedProductItems = document.querySelectorAll('.options-panel-recommended-product-item');
    for (let item of recommendedProductItems) {
      const img = item.querySelector('.options-panel-recommended-product-image img');
      if (img && item.dataset.variantId === String(variantId)) {
        const imageUrl =
          img.currentSrc ||
          img.getAttribute('src') ||
          img.dataset.srcset ||
          '';

        if (imageUrl) {
          return imageUrl;
        }
      }
    }
    
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMwIiBoZWlnaHQ9IjMwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xNSA4QzEyLjc5IDggMTEgOS43OSAxMSAxMkMxMSAxNC4yMSAxMi43OSAxNiAxNSAxNkMxNy4yMSAxNiAxOSAxNC4yMSAxOSAxMkMxOSA5Ljc5IDE3LjIxIDggMTUgOFpNMTUgMTQuNUMxMy42MiAxNC41IDEyLjUgMTMuMzggMTIuNSAxMkMxMi41IDEwLjYyIDEzLjYyIDkuNSAxNSA5LjVDMTYuMzggOS41IDE3LjUgMTAuNjIgMTcuNSAxMkMxNy41IDEzLjM4IDE2LjM4IDE0LjUgMTUgMTQuNVoiIGZpbGw9IiNDQ0MiLz4KPHBhdGggZD0iTTIyIDIwSDhDNy40NSAyMCA3IDIwLjQ1IDcgMjFWMjJDNyAyMi41NSA3LjQ1IDIzIDggMjNIMjJDMjIuNTUgMjMgMjMgMjIuNTUgMjMgMjJWMjFDMjMgMjAuNDUgMjIuNTUgMjAgMjIgMjBaIiBmaWxsPSIjQ0NDIi8+Cjwvc3ZnPgo='; // 默认占位图
  }
  
  /**
   * 获取产品名称
   */
  getProductName(variantId) {
    // 从推荐产品选择器获取产品名称
    const recommendedItems = document.querySelectorAll(`[data-variant-id="${variantId}"]`);
    for (let item of recommendedItems) {
      const title = item.querySelector('.recommended-product-title');
      if (title) {
        return title.textContent.trim();
      }
    }
    
    // 尝试从推荐产品列表项获取名称
    const recommendedProductItems = document.querySelectorAll('.options-panel-recommended-product-item');
    for (let item of recommendedProductItems) {
      const title = item.querySelector('.options-panel-recommended-product-name');
      if (title && item.dataset.variantId === variantId.toString()) {
        return title.textContent.trim();
      }
    }
    
    return 'Recommended Product';
  }
  
  /**
   * 更新价格显示
   */
  updatePriceDisplay() {
    if (!this.state.currentVariant) return;
    
    // 更新主产品项的价格
    const mainItem = this.querySelector('product-option-item[data-product-id="' + this.productId + '"]');
    if (mainItem) {
      const priceElement = mainItem.querySelector('.product-option-current-price');
      const comparePriceElement = mainItem.querySelector('.product-option-compare-price');
      
      if (priceElement) {
        priceElement.textContent = this.formatMoney(this.state.currentVariant.price);
      }
      
      if (comparePriceElement) {
        if (this.state.currentVariant.compare_at_price > this.state.currentVariant.price) {
          comparePriceElement.textContent = this.formatMoney(this.state.currentVariant.compare_at_price);
          comparePriceElement.style.display = 'inline';
        } else {
          comparePriceElement.style.display = 'none';
        }
      }
      
      // 更新数据属性
      mainItem.dataset.variantId = this.state.currentVariant.id;
      mainItem.dataset.price = this.state.currentVariant.price;
      mainItem.dataset.comparePrice = this.state.currentVariant.compare_at_price || 0;
    }
    
    // 计算总价
    let totalPrice = this.state.currentVariant.price;
    
    // 添加推荐产品价格
    this.state.recommendedProducts.forEach(product => {
      const priceInfo = this.getProductPriceInfo(product.variantId);
      if (priceInfo && priceInfo.price) {
        totalPrice += priceInfo.price * product.quantity;
      }
    });
    
    /* 已注释特惠配送和普通配送价格计算
    // 添加特惠配送价格（只有在邮箱验证通过且可以添加特惠配送时）
    if (this.state.canAddSpecialDelivery && this.state.specialDelivery && this.state.specialDelivery.price) {
      totalPrice += this.state.specialDelivery.price;
    }
    
    // 添加普通配送价格（只有在可以添加普通配送时）
    if (this.state.canAddNormalDelivery && this.state.normalDelivery && this.state.normalDelivery.price) {
      totalPrice += this.state.normalDelivery.price;
    }
    */
    
    // 更新总价显示
    const totalContainer = this.querySelector('.product-options-total');
    if (totalContainer) {
      totalContainer.textContent = this.formatMoney(totalPrice);
    }
    
    // 更新产品标题（如果变体有标题）
    if (this.state.currentVariant.title && this.state.currentVariant.title !== 'Default Title') {
      const titleElement = mainItem?.querySelector('.product-option-title');
      if (titleElement) {
        const baseTitle = titleElement.getAttribute('data-base-title') || titleElement.textContent;
        if (!titleElement.getAttribute('data-base-title')) {
          titleElement.setAttribute('data-base-title', baseTitle);
        }
        titleElement.textContent = `${baseTitle} - ${this.state.currentVariant.title}`;
      }
    }
  }
  
  /**
   * 格式化价格
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    const value = cents / 100;
    return '€' + value.toFixed(2);
  }
}

// 注册自定义元素
customElements.define('product-options-panel', ProductOptionsPanel);

/**
 * 产品选项项目组件
 * 用于显示单个产品选项项
 */
class ProductOptionItem extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.productId = this.dataset.productId;
    this.price = parseFloat(this.dataset.price) || 0;
    this.comparePrice = parseFloat(this.dataset.comparePrice) || 0;
    
    this.init();
  }
  
  init() {
    this.attachEventListeners();
  }
  
  attachEventListeners() {
    // 如果有选择按钮，添加点击事件
    const selectButton = this.querySelector('.option-select-button');
    if (selectButton) {
      selectButton.addEventListener('click', () => {
        this.select();
      });
    }
  }
  
  select() {
    // 触发选择事件
    this.dispatchEvent(new CustomEvent('option:selected', {
      bubbles: true,
      detail: {
        variantId: this.variantId,
        productId: this.productId,
        price: this.price,
        comparePrice: this.comparePrice
      }
    }));
  }
}

// 注册选项项目组件
customElements.define('product-option-item', ProductOptionItem); 